/**
 * Kiosk Setup - signs this device in as one of the restaurant's kiosks.
 *
 * The owner / manager signs in with the ONE restaurant-account sign-in every
 * PWA uses (components/pwa/PwaLogin: email + password, the SMS / authenticator
 * code when 2FA is on, or Google), or enters a one-time pairing code from Back
 * Office → Kiosks. The account session is used once and dropped
 * (persist={false}): the kiosk is customer-facing, so it keeps only its own
 * device token and customers never get an account session.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, KeyRound, Loader2, Unplug, ArrowLeft } from 'lucide-react';
import PaymentStatusPanel, { PaymentStatusDetails } from '@/components/payments/PaymentStatusPanel';
import { usePaymentStatusPanel, kioskPanelFetcher } from '@/components/payments/usePaymentStatusPanel';
import { kioskPath, READER_UNAVAILABLE } from '../utils/kioskPath';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import PwaLogin from '@/components/pwa/PwaLogin';
import { apiErrorText } from '@/lib/pwaAccount';
import { useKiosk } from '../context/KioskContext';

/** This kiosk's card reader, as Aster Gateway reads it (docs/POS_PAYMENT_STATUS_PANEL.md, Kiosk). */
function ReaderStatus({ status }) {
  const readiness = status.panel?.payment;
  const tone = status.summary.tone;
  const dot = { online: 'bg-emerald-400', offline: 'bg-red-400', checking: 'bg-sky-400 animate-pulse' }[tone] || 'bg-slate-400';
  return (
    <div className="rounded-xl mb-6 border border-white/10 overflow-hidden" style={{ background: '#262d3d' }} data-testid="kiosk-reader-status">
      <div className="px-4 py-4 border-b border-white/[0.06] flex items-center justify-between gap-3">
        <div>
          <p className="text-white font-semibold">Card reader</p>
          <p className="text-white/50 text-xs mt-0.5" title={status.summary.reason}>{status.summary.reason}</p>
        </div>
        <span className="flex items-center gap-2 text-sm font-semibold text-white/80 shrink-0" data-testid="kiosk-reader-state" data-tone={tone}>
          <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />{status.summary.label}
        </span>
      </div>
      {readiness && (
        <div className={`px-4 py-3 border-b border-white/[0.06] text-sm ${readiness.card_ready ? 'text-emerald-300' : 'text-red-300'}`}
          data-testid="kiosk-card-checkout">
          {readiness.card_ready
            ? (readiness.reader_status_unknown ? 'Card checkout is on (the reader status could not be confirmed).' : 'Card checkout is ready.')
            : <>{READER_UNAVAILABLE}{(readiness.reasons || []).filter((r) => !/card reader unavailable/i.test(r)).map((r) => (
                <span key={r} className="block text-white/50 text-xs mt-1">{r}</span>))}</>}
        </div>
      )}
      <PaymentStatusDetails status={status} />
    </div>
  );
}

export default function SetupPage() {
  const { deviceId, deviceToken, paired, pairKiosk, pairWithManagerToken, forgetPairing, config } = useKiosk();
  const navigate = useNavigate();
  const fetchPanel = useMemo(() => kioskPanelFetcher({ deviceId, deviceToken }), [deviceId, deviceToken]);
  const status = usePaymentStatusPanel(fetchPanel, { enabled: paired });
  const [mode, setMode] = useState('login'); // login | code
  const [busy, setBusy] = useState(false);
  const [kioskName, setKioskName] = useState('');
  const [code, setCode] = useState('');

  const finish = () => toast.success('Kiosk is set up');

  // PwaLogin answered (persist={false}): set the kiosk up with that one-time session.
  const handleSignedIn = async (_account, loginResponse, restaurantId) => {
    const primary = loginResponse.primary_restaurant_id;
    const other = restaurantId && String(restaurantId) !== String(primary) ? restaurantId : null;
    await pairWithManagerToken(loginResponse.access_token, kioskName.trim(), other);
    finish();
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await pairKiosk(code);
      finish();
    } catch (error) {
      toast.error(apiErrorText(error, 'Pairing failed'));
    } finally {
      setBusy(false);
    }
  };

  const card = 'rounded-xl p-6 mb-6 border border-white/10';
  const inputCls = 'bg-white/5 border-white/10 text-white h-12';

  if (!paired && mode === 'login') {
    return (
      <PwaLogin
        appName="Kiosk Setup"
        subtitle="An owner or manager signs in once with the restaurant account to set up this kiosk."
        icon={Monitor}
        accent="#f5a623"
        persist={false}
        canSubmit={!!kioskName.trim()}
        onSignedIn={handleSignedIn}
        footer={(
          <div className="text-center space-y-2">
            <p className="text-white/40 text-xs">Device {deviceId}</p>
            <button type="button" onClick={() => setMode('code')} data-testid="setup-mode-code"
              className="text-sm text-white/70 inline-flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> Use a pairing code instead
            </button>
          </div>
        )}
      >
        <Input className={inputCls} placeholder="Kiosk name (e.g. Front Entrance)" value={kioskName}
          onChange={e => setKioskName(e.target.value)} data-testid="kiosk-name-input" />
      </PwaLogin>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ background: '#1e2330' }}>
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
          {paired && (
            <button type="button" onClick={() => navigate(kioskPath(''))} aria-label="Back to the kiosk"
              className="w-10 h-10 -ml-2 mr-1 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <Monitor className="w-6 h-6" style={{ color: '#f5a623' }} /> Kiosk Setup
          {paired && <span className="ml-auto"><PaymentStatusPanel status={status} /></span>}
        </h1>
        <p className="text-white/50 text-sm mb-8">Device {deviceId}</p>

        {paired && <ReaderStatus status={status} />}

        {paired ? (
          <div className={card} style={{ background: '#262d3d' }}>
            <p className="text-white mb-1">This kiosk is set up{config?.restaurant?.name ? ` for ${config.restaurant.name}` : ''}.</p>
            <p className="text-white/50 text-sm mb-4">To move it to another restaurant, sign it out here, or unpair it from Back Office → Kiosks.</p>
            <Button variant="outline" onClick={forgetPairing} className="border-white/20 text-white" data-testid="kiosk-sign-out">
              <Unplug className="w-4 h-4 mr-2" /> Sign this kiosk out
            </Button>
          </div>
        ) : (
          <form onSubmit={handleCode} className={`${card} space-y-4`} style={{ background: '#262d3d' }}>
            <p className="text-white/60 text-sm">Create a code in Back Office → Kiosks → Pair a kiosk. It works once, for 15 minutes.</p>
            <Input className={`${inputCls} text-center tracking-[0.3em] text-xl uppercase`} placeholder="ABCD-2345"
              value={code} onChange={e => setCode(e.target.value)} data-testid="pairing-code-input" />
            <Button type="submit" disabled={busy || code.replace(/[^a-z0-9]/gi, '').length < 8} className="w-full h-12 text-[#0f1419]" style={{ background: '#f5a623' }}>
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Pair kiosk'}
            </Button>
            <button type="button" onClick={() => setMode('login')} className="w-full text-white/60 text-sm" data-testid="setup-mode-login">
              Sign in with the restaurant account instead
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
